import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Stack,
  CircularProgress,
  Button,
  Divider,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Alert,
  IconButton,
  Paper,
  Card,
  CardActionArea,
  CardContent,
  Avatar,
  Tooltip,
} from "@mui/material";

import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";

import axios from "axios";
import Images from "../../../utils/Images";
import { CategoryFilterChip } from "../../../components/FilterChip";
import { API } from "../../../utils/Api";

const todayYYYYMMDD = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

function OnlineItemSales() {
  const token = localStorage.getItem("token");
  const [outletData] = useState(JSON.parse(localStorage.getItem("outletData")));

  // Left panel
  const [selectedChipIndex, setSelectedChipIndex] = useState(null);
  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Right panel (sale builder)
  const [platform, setPlatform] = useState("ZOMATO");
  const [date, setDate] = useState(todayYYYYMMDD());

  // current item config
  const [qty, setQty] = useState(1);
  const [price, setPrice] = useState(0);
  const [discount, setDiscount] = useState(0);

  // addon selections
  const [addonSelections, setAddonSelections] = useState({});

  // cart items
  const [cartItems, setCartItems] = useState([]);

  // submit state
  const [posting, setPosting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const flattenCategoriesToProducts = (categoryList) => {
    if (!Array.isArray(categoryList)) return [];
    return categoryList.flatMap((cat) =>
      (cat.products || []).map((prod) => ({
        ...prod,
        category: { _id: cat._id, name: cat.name, description: cat.description },
      }))
    );
  };

  const filterProducts = (searchText = "", categoryId = null) => {
    const search = (searchText || "").toLowerCase();
    let filtered = allProducts;

    if (categoryId) {
      filtered = filtered.filter((p) => p.category && p.category._id === categoryId);
    }

    if (search) {
      filtered = filtered.filter((p) => {
        const nameMatch = p.name?.toLowerCase().includes(search);
        const descMatch = p.description?.toLowerCase().includes(search);
        const keywordsMatch = p.keywords?.toLowerCase().includes(search);
        return nameMatch || descMatch || keywordsMatch;
      });
    }

    setProducts(filtered);
  };

  const handleChipClick = (categoryId, index) => {
    if (index === selectedChipIndex) {
      setSelectedChipIndex(null);
      filterProducts(searchQuery, null);
    } else {
      setSelectedChipIndex(index);
      filterProducts(searchQuery, categoryId);
    }
  };

  const handleSearch = (searchText) => {
    setSearchQuery(searchText);

    let categoryId = null;
    if (selectedChipIndex !== null && categories[selectedChipIndex]) {
      categoryId = categories[selectedChipIndex]._id;
    }

    filterProducts(searchText, categoryId);
  };

  const getCategoriesProductsList = async () => {
    if (!outletData?._id) return;

    try {
      setLoadingProducts(true);

      const response = await axios.get(`${API.BASE_URL}product/getProductsWithAddons`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        params: { outletId: outletData._id },
      });

      const fetchedCategories = response?.data?.data?.categories || [];
      setCategories(fetchedCategories);

      const flattened = flattenCategoriesToProducts(fetchedCategories);
      setAllProducts(flattened);
      setProducts(flattened);
    } catch (e) {
      console.error("Error fetching products with addons:", e);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    getCategoriesProductsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When product changes, reset builder UI
  useEffect(() => {
    setAddonSelections({});
    setQty(1);
    setDiscount(0);

    const p =
      selectedProduct?.newOfferPrice ??
      selectedProduct?.offerPrice ??
      selectedProduct?.defaultOfferPrice ??
      selectedProduct?.price ??
      0;

    setPrice(num(p));
  }, [selectedProduct]);

  const currentTotal = useMemo(() => {
    return Math.max(0, num(qty) * num(price) - num(discount));
  }, [qty, price, discount]);

  const cartGrandTotal = useMemo(() => {
    return cartItems.reduce((sum, item) => sum + num(item.total), 0);
  }, [cartItems]);

  const getValidAddonValues = (addon) => {
    if (!addon?.values?.length) return [];

    return addon.values.filter((v) => {
      const isActiveOk = v?.status !== "INACTIVE" || v?.isDefault;
      const notExcluded = !v?.excludedOutlets?.includes(outletData?._id) || v?.isDefault;
      const notIconInfo = !v?.iconInfo;
      return isActiveOk && notExcluded && notIconInfo;
    });
  };

  const selectAddonValue = (addon, value) => {
    setAddonSelections((prev) => ({
      ...prev,
      [addon._id]: {
        addonId: addon._id,
        addonValueId: value._id,
        addonTitle: addon.title,
        addonValueTitle: value.subLabel || value.value || "",
      },
    }));
  };

  const validateCurrentItem = () => {
    if (!selectedProduct?._id) return "Please select a product.";
    if (selectedProduct?.isExhausted) return "Product is exhausted.";
    if (num(qty) <= 0) return "Quantity must be greater than 0.";

    if (selectedProduct?.hasAddon && Array.isArray(selectedProduct?.addons)) {
      const requiredAddons = selectedProduct.addons
        .filter((a) => !a?.excludedOutlets?.includes(outletData?._id))
        .filter((a) => a?.isOptional === false);

      for (const addon of requiredAddons) {
        const validValues = getValidAddonValues(addon);
        if (!validValues.length) continue;
        if (!addonSelections[addon._id]) return `Select required addon: ${addon.title}`;
      }
    }

    return null;
  };

  const handleAddItemToCart = () => {
    setSuccessMsg("");
    setErrorMsg("");

    const err = validateCurrentItem();
    if (err) return setErrorMsg(err);

    const addonsArray = Object.values(addonSelections);

    const payloadItem = {
      platform,
      date,
      product: selectedProduct._id,
      productName: selectedProduct.name,
      addons: addonsArray,
      quantity: num(qty),
      price: num(price),
      discount: num(discount),
      total: num(currentTotal),
    };

    setCartItems((prev) => [...prev, payloadItem]);
    setSuccessMsg("Item added to sale.");
  };

  const removeCartItem = (index) => {
    setCartItems((prev) => prev.filter((_, i) => i !== index));
  };

  const submitSale = async () => {
    setSuccessMsg("");
    setErrorMsg("");

    if (!cartItems.length) return setErrorMsg("Add at least 1 item to submit.");

    try {
      setPosting(true);

      for (let i = 0; i < cartItems.length; i++) {
        const item = cartItems[i];

        const body = {
          platform: item.platform,
          date: item.date,
          product: item.product,
          addons: item.addons,
          quantity: item.quantity,
          price: item.price,
          discount: item.discount,
          total: item.total,
        };

        await axios.post(`${API.BASE_URL}third-party-sales`, body, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
      }

      setSuccessMsg("Third-party sale posted successfully.");
      setCartItems([]);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Posting failed";
      setErrorMsg(msg);
    } finally {
      setPosting(false);
    }
  };

  const selectedCategoryId =
    selectedChipIndex !== null && categories[selectedChipIndex]
      ? categories[selectedChipIndex]._id
      : null;

  return (
    <Box sx={{ height: "100vh", backgroundColor: "#f5f6f8" }}>
      <Grid container sx={{ height: "100%", paddingTop: '120px'  }}>
        {/* LEFT */}
        <Grid item md={8} sx={{ height: "100%", p: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              height: "100%",
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid #e6e6e6",
              backgroundColor: "#fff",
            }}
          >
            {/* Sticky header */}
            <Box
              sx={{
                p: 2,
                borderBottom: "1px solid #eee",
                position: "sticky",
                top: 0,
                zIndex: 2,
                background: "#fff",
              }}
            >
              <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 1 }}>
                Products
              </Typography>

              <TextField
                fullWidth
                size="small"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ backgroundColor: "#fafafa", borderRadius: 2 }}
              />

              {/* Categories in a single horizontal row */}
              <Box
                sx={{
                  mt: 1.5,
                  display: "flex",
                  gap: 1,
                  overflowX: "auto",
                  pb: 0.5,
                }}
              >
                {categories.map((category, index) => (
                  <Box key={category._id} sx={{ flex: "0 0 auto" }}>
                    <CategoryFilterChip
                      filterText={category.name}
                      isSelected={selectedChipIndex === index}
                      onClick={() => handleChipClick(category._id, index)}
                    />
                  </Box>
                ))}

                {selectedCategoryId && (
                  <Chip
                    label="Clear"
                    variant="outlined"
                    onClick={() => {
                      setSelectedChipIndex(null);
                      filterProducts(searchQuery, null);
                    }}
                    sx={{ flex: "0 0 auto" }}
                  />
                )}
              </Box>
            </Box>

            {/* Body */}
            <Box sx={{ p: 2, height: "calc(100% - 120px)", overflow: "auto" }}>
              {loadingProducts ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <CircularProgress sx={{ color: "#111" }} />
                  <Typography sx={{ mt: 1, opacity: 0.7 }}>Fetching products...</Typography>
                </Box>
              ) : products.length ? (
                <Grid container spacing={2}>
                  {products.map((product) => {
                    const active = selectedProduct?._id === product._id;

                    return (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={product._id}>
                        <Card
                          variant="outlined"
                          sx={{
                            borderRadius: 3,
                            overflow: "hidden",
                            borderColor: active ? "#00A4E2" : "#eee",
                            boxShadow: active ? "0 0 0 2px rgba(0,164,226,0.25)" : "none",
                            backgroundColor: product.isExhausted ? "#FFF4F4" : "#fff",
                          }}
                        >
                          <CardActionArea onClick={() => setSelectedProduct(product)}>
                            <CardContent sx={{ p: 1.5 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                                <Avatar
                                  variant="rounded"
                                  src={product.displayImageUrl || product.displayImage}
                                  alt={product.name}
                                  imgProps={{
                                    onError: (e) => {
                                      e.currentTarget.onerror = null;
                                      e.currentTarget.src = Images.product;
                                    },
                                  }}
                                  sx={{ width: 52, height: 52, borderRadius: 2 }}
                                />

                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography
                                    sx={{
                                      fontSize: 14,
                                      fontWeight: 800,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {product.name}
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: 12,
                                      opacity: 0.7,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  >
                                    {product.category?.name || "-"}
                                  </Typography>
                                </Box>

                                {product.isExhausted ? (
                                  <Tooltip title="Exhausted">
                                    <ReportProblemIcon sx={{ color: "#d32f2f" }} />
                                  </Tooltip>
                                ) : null}
                              </Box>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              ) : (
                <Typography sx={{ textAlign: "center", py: 6, opacity: 0.7 }}>
                  No products available.
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* RIGHT */}
        <Grid item md={4} sx={{ height: "100%", p: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              height: "100%",
              borderRadius: 3,
              border: "1px solid #e6e6e6",
              backgroundColor: "#fff",
              overflow: "auto",
            }}
          >
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ShoppingCartOutlinedIcon />
                <Typography sx={{ fontSize: 18, fontWeight: 900 }}>
                  Online Sale
                </Typography>
                <Typography sx={{ ml: "auto", fontSize: 12, opacity: 0.6 }}>
                  Third-party
                </Typography>
              </Box>

              {(successMsg || errorMsg) && (
                <Box sx={{ mt: 1.2 }}>
                  {successMsg ? (
                    <Alert severity="success" sx={{ mb: 1 }}>
                      {successMsg}
                    </Alert>
                  ) : null}
                  {errorMsg ? <Alert severity="error">{errorMsg}</Alert> : null}
                </Box>
              )}

              <Box sx={{ mt: 1.5, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="platform-label">Platform</InputLabel>
                  <Select
                    labelId="platform-label"
                    value={platform}
                    label="Platform"
                    onChange={(e) => setPlatform(e.target.value)}
                  >
                    <MenuItem value="ZOMATO">ZOMATO</MenuItem>
                    <MenuItem value="SWIGGY">SWIGGY</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  fullWidth
                  label="Date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              {!selectedProduct ? (
                <Box sx={{ textAlign: "center", py: 6 }}>
                  <Typography sx={{ fontSize: 14, opacity: 0.75, mb: 1 }}>
                    Select a product to start
                  </Typography>
                  <img
                    src={Images.noProductSelected}
                    alt="product-not-selected"
                    style={{ maxWidth: 220, opacity: 0.9 }}
                  />
                </Box>
              ) : (
                <>
                  <Box sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 900 }}>
                      {selectedProduct.name}
                    </Typography>
                    <Typography sx={{ fontSize: 12, opacity: 0.7 }}>
                      {selectedProduct.category?.name || "-"}
                    </Typography>
                  </Box>

                  {/* Inputs */}
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.2 }}>
                    <TextField
                      size="small"
                      label="Qty"
                      type="number"
                      value={qty}
                      onChange={(e) => setQty(e.target.value)}
                      inputProps={{ min: 1 }}
                    />
                    <TextField
                      size="small"
                      label="Price"
                      type="number"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      inputProps={{ min: 0 }}
                    />
                    <TextField
                      size="small"
                      label="Discount"
                      type="number"
                      value={discount}
                      onChange={(e) => setDiscount(e.target.value)}
                      inputProps={{ min: 0 }}
                    />
                    <TextField
                      size="small"
                      label="Total"
                      value={currentTotal}
                      InputProps={{ readOnly: true }}
                    />
                  </Box>

                  {/* Addons */}
                  {selectedProduct?.hasAddon && Array.isArray(selectedProduct?.addons) ? (
                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 900, mb: 1 }}>
                        Addons
                      </Typography>

                      <Stack spacing={1}>
                        {selectedProduct.addons
                          .filter((a) => !a?.excludedOutlets?.includes(outletData?._id))
                          .map((addon) => {
                            const values = getValidAddonValues(addon);
                            if (!values.length) return null;

                            const selected = addonSelections?.[addon._id]?.addonValueId;

                            return (
                              <Box
                                key={addon._id}
                                sx={{
                                  p: 1.2,
                                  borderRadius: 2,
                                  border: "1px solid #eee",
                                  backgroundColor: "#fafafa",
                                }}
                              >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                  <Typography sx={{ fontSize: 13, fontWeight: 800 }}>
                                    {addon.title}
                                  </Typography>
                                  {!addon.isOptional && (
                                    <Chip
                                      size="small"
                                      label="Required"
                                      sx={{ height: 20, fontSize: 11 }}
                                    />
                                  )}
                                </Box>

                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                  {values.map((v) => {
                                    const active = selected === v._id;
                                    return (
                                      <Chip
                                        key={v._id}
                                        label={`${v.subLabel || v.value}${
                                          num(v.price) ? `  +₹${v.price}` : ""
                                        }`}
                                        onClick={() => selectAddonValue(addon, v)}
                                        variant={active ? "filled" : "outlined"}
                                        sx={{
                                          borderRadius: 999,
                                          ...(active
                                            ? { backgroundColor: "#00A4E2", color: "#fff" }
                                            : { borderColor: "#00A4E2", color: "#00A4E2" }),
                                        }}
                                      />
                                    );
                                  })}
                                </Box>
                              </Box>
                            );
                          })}
                      </Stack>
                    </Box>
                  ) : null}

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleAddItemToCart}
                    sx={{
                      mt: 2,
                      borderRadius: 2,
                      backgroundColor: "#111",
                      py: 1.1,
                      fontWeight: 800,
                      textTransform: "none",
                    }}
                  >
                    Add Item
                  </Button>
                </>
              )}

              {/* Cart */}
              <Divider sx={{ my: 2 }} />
              <Typography sx={{ fontSize: 14, fontWeight: 900, mb: 1 }}>
                Sale Items <span style={{ opacity: 0.6 }}>({cartItems.length})</span>
              </Typography>

              {cartItems.length === 0 ? (
                <Typography sx={{ fontSize: 13, opacity: 0.7 }}>
                  No items added yet.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {cartItems.map((item, idx) => (
                    <Box
                      key={`${item.product}-${idx}`}
                      sx={{
                        p: 1.2,
                        border: "1px solid #eee",
                        borderRadius: 2,
                        backgroundColor: "#fff",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 900,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {item.productName}
                          </Typography>
                          <Typography sx={{ fontSize: 12, opacity: 0.7 }}>
                            Qty {item.quantity} • ₹{item.price} • Disc ₹{item.discount}
                          </Typography>
                        </Box>

                        <Typography sx={{ fontSize: 13, fontWeight: 900 }}>
                          ₹{item.total}
                        </Typography>

                        <IconButton size="small" onClick={() => removeCartItem(idx)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      {item.addons?.length ? (
                        <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                          {item.addons.map((a) => (
                            <Chip
                              key={`${a.addonId}-${a.addonValueId}`}
                              size="small"
                              label={`${a.addonTitle}: ${a.addonValueTitle}`}
                            />
                          ))}
                        </Box>
                      ) : null}
                    </Box>
                  ))}
                </Stack>
              )}

              <Box
                sx={{
                  mt: 2,
                  p: 1.2,
                  borderRadius: 2,
                  backgroundColor: "#fafafa",
                  border: "1px solid #eee",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography sx={{ fontWeight: 900, fontSize: 13 }}>Grand Total</Typography>
                <Typography sx={{ fontWeight: 900, fontSize: 13 }}>₹{cartGrandTotal}</Typography>
              </Box>

              <Button
                fullWidth
                variant="contained"
                disabled={posting || cartItems.length === 0}
                onClick={submitSale}
                sx={{
                  mt: 1.5,
                  borderRadius: 2,
                  backgroundColor: "#00A4E2",
                  py: 1.1,
                  fontWeight: 900,
                  textTransform: "none",
                }}
              >
                {posting ? "Posting..." : "Submit Sale"}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default OnlineItemSales;
